import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, Field, Toggle, btn, btnGhost, inputClass } from "@/pages/admin/email/EmailLayout";

export default function EmailAutoresponder() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get("/admin/email/settings").then((r) => setSettings(r.data));
    api.get("/admin/email/sends", { params: { limit: 100 } }).then((r) =>
      setLog(r.data.filter((s) => s.kind.startsWith("welcome")))
    );
  }, []);

  if (!settings) return null;

  const save = async () => {
    const { data } = await api.patch("/admin/email/settings", {
      autoresponder_enabled: settings.autoresponder_enabled,
      welcome_subject: settings.welcome_subject,
      welcome_body: settings.welcome_body,
    });
    setSettings(data);
    toast.success("Autoresponder saved");
  };

  const showPreview = async () => {
    await save();
    const { data } = await api.get("/admin/email/autoresponder/preview");
    setPreview(data.html);
  };

  const sendTest = async () => {
    await save();
    try {
      const { data } = await api.post("/admin/email/autoresponder/test");
      toast.success(`Test welcome email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 10000 });
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start" data-testid="email-autoresponder">
      <Card title="Welcome email" testid="autoresponder-editor">
        <Toggle
          checked={settings.autoresponder_enabled}
          onChange={(v) => setSettings((s) => ({ ...s, autoresponder_enabled: v }))}
          label="Send the welcome email automatically"
          description="Sent once, immediately after a subscriber confirms their address. Never sent twice to the same person."
          testid="autoresponder-toggle"
        />
        <Field label="Subject line">
          <input
            value={settings.welcome_subject}
            onChange={(e) => setSettings((s) => ({ ...s, welcome_subject: e.target.value }))}
            data-testid="autoresponder-subject"
            className={inputClass}
          />
        </Field>
        <Field
          label="Message"
          hint="Plain text. Blank lines become paragraphs, and https:// addresses become links. The footer with sender details and the unsubscribe link is added automatically."
        >
          <textarea
            rows={16}
            value={settings.welcome_body}
            onChange={(e) => setSettings((s) => ({ ...s, welcome_body: e.target.value }))}
            data-testid="autoresponder-body"
            className={`${inputClass} leading-relaxed`}
          />
        </Field>
        <div className="flex flex-wrap gap-3">
          <button onClick={save} className={btn} data-testid="autoresponder-save">
            Save
          </button>
          <button onClick={showPreview} className={btnGhost} data-testid="autoresponder-preview">
            Preview
          </button>
          <button onClick={sendTest} className={btnGhost} data-testid="autoresponder-test">
            Send test to me
          </button>
        </div>
      </Card>

      <div className="space-y-8">
        <Card title="Welcome email history" testid="autoresponder-history">
          {log.length === 0 && (
            <p className="text-[13px] font-light text-[#8A867F]">No welcome emails sent yet.</p>
          )}
          {log.map((l) => (
            <div
              key={l.id}
              className="flex items-baseline justify-between py-2 border-b border-line last:border-0 text-[12.5px] font-light text-[#5A5A5A]"
            >
              <span className="break-all pr-3">{l.email}</span>
              <span>
                {l.status} · {String(l.created_at).slice(0, 10)}
              </span>
            </div>
          ))}
        </Card>
        {preview && (
          <Card title="Preview" testid="autoresponder-preview-panel">
            <iframe
              title="Welcome email preview"
              srcDoc={preview}
              className="w-full h-[560px] border border-line bg-white"
            />
          </Card>
        )}
      </div>
    </div>
  );
}
